import { ColumnDef } from "@tanstack/react-table";
import { Product } from "@/types/Products";
import { Checkbox } from "@/components/ui/checkbox";
import StatusBadge from "@/components/StatusBadge";
import { Combine } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import ProductActions from "@/components/products/product-table/ProductActions";
import ProductSelectionDropdown from "@/components/products/product-table/ProductSelectionDropdown";

interface UseProductTableColumnsProps {
    selectedProducts: Product[];
    totalCount: number;
    products: Product[];
    setSelectedProducts: (products: Product[]) => void;
    setPageSelections: (
        selections: Record<number, Record<string, boolean>>
    ) => void;
    handleSelectAll: () => Promise<void>;
    fetchProducts: () => Promise<void>;
    markets: string[];
}

export const useProductTableColumns = ({
    selectedProducts,
    totalCount,
    products,
    setSelectedProducts,
    setPageSelections,
    handleSelectAll,
    fetchProducts,
    markets,
}: UseProductTableColumnsProps): ColumnDef<Product>[] => {
    const columns: ColumnDef<Product>[] = [
        {
            id: "select",
            header: ({ table: tableInstance }) => (
                <ProductSelectionDropdown
                    tableInstance={tableInstance}
                    selectedProducts={selectedProducts}
                    totalCount={totalCount}
                    products={products}
                    setSelectedProducts={setSelectedProducts}
                    setPageSelections={setPageSelections}
                    handleSelectAll={handleSelectAll}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={
                        row.getIsSelected() ||
                        selectedProducts.some(
                            (product) =>
                                product.shopify_variant_id ===
                                row.original.shopify_variant_id
                        )
                    }
                    onCheckedChange={(value) => {
                        row.toggleSelected(!!value);
                        if (value) {
                            setSelectedProducts((prev) => [
                                ...prev,
                                row.original,
                            ]);
                        } else {
                            setSelectedProducts((prev) =>
                                prev.filter(
                                    (p) =>
                                        p.shopify_variant_id !==
                                        row.original.shopify_variant_id
                                )
                            );
                        }
                    }}
                    aria-label="Select row"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "product_name",
            header: "Product",
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    {row.original.is_merged && (
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Combine className="h-4 w-4 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Merged product</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    )}
                    <TooltipProvider delayDuration={100}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className="max-w-[200px] truncate cursor-default">
                                    {row.getValue("product_name")}
                                </div>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{row.getValue("product_name")}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            ),
            filterFn: (row, id, filterValue) => {
                const productName = row.getValue("product_name") as string;
                const variantName = row.getValue("variant_name") as string;
                const sku = (row.getValue("sku") as string) || "";

                if (!filterValue) return true;

                const searchValue = filterValue.toLowerCase();
                return (
                    productName.toLowerCase().includes(searchValue) ||
                    variantName.toLowerCase().includes(searchValue) ||
                    sku.toLowerCase().includes(searchValue)
                );
            },
        },
        {
            accessorKey: "variant_name",
            header: "Variant",
            cell: ({ row }) => (
                <TooltipProvider delayDuration={100}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="max-w-[200px] truncate cursor-default">
                                {row.getValue("variant_name")}
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{row.getValue("variant_name")}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ),
            enableColumnFilter: false,
        },
        {
            accessorKey: "sku",
            header: "SKU",
            cell: ({ row }) => (
                <div
                    className="max-w-[200px] truncate"
                    title={row.getValue("sku")}
                >
                    {row.getValue("sku")}
                </div>
            ),
        },
        {
            accessorKey: "price",
            header: () => <div>Current Price</div>,
            cell: ({ row }) => {
                const amount = parseFloat(row.getValue("price"));
                const formatted = new Intl.NumberFormat("en-GB", {
                    style: "currency",
                    currency: "GBP",
                }).format(amount);

                return <div className="font-medium">{formatted}</div>;
            },
            filterFn: (row, id, filterValue: [number, number]) => {
                const price = parseFloat(row.getValue(id));
                const [min, max] = filterValue;
                return price >= min && price <= max;
            },
        },
        {
            accessorKey: "last_optimized",
            header: "Last Optimized",
            cell: ({ row }) => {
                const date = new Date(row.getValue("last_optimized"));
                if (date.getTime() === 0) return <div>Never</div>;
                return <div>{date.toLocaleDateString()}</div>;
            },
            enableSorting: true,
        },
        {
            accessorKey: "is_converged",
            enableHiding: true,
            header: "Converged",
        },
        {
            accessorKey: "optimization_data",
            enableHiding: true,
            header: "Optimization Data",
        },
        {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => {
                const status = row.getValue("status") as Product["status"];
                const is_converged = row.getValue("is_converged") as boolean;
                return (
                    <StatusBadge
                        status={
                            is_converged && status === "Optimizing"
                                ? "Ready"
                                : status
                        }
                    />
                );
            },
        },
        {
            accessorKey: "category",
            header: "Category",
            cell: ({ row }) => {
                const category = row.getValue("category") as string;
                return <div>{category || "Uncategorized"}</div>;
            },
        },
        {
            accessorKey: "product_type",
            header: "Product Type",
            cell: ({ row }) => {
                const productType = row.getValue("product_type") as string;
                return <div>{productType || "Uncategorized"}</div>;
            },
        },
        {
            accessorKey: "tags",
            header: "Tags",
            cell: ({ row }) => {
                const tags = row.getValue("tags") as string[];
                return <div>{tags?.join(", ") || "No tags"}</div>;
            },
            enableColumnFilter: true,
            enableGlobalFilter: false,
            filterFn: undefined,
        },
        {
            id: "actions",
            enableHiding: false,
            cell: ({ row }) => (
                <ProductActions
                    row={row}
                    onRefresh={fetchProducts}
                    markets={markets}
                />
            ),
        },
    ];

    return columns;
};
